import {
  useEffect,
  createContext,
  useContext,
  ReactNode,
  useState,
} from 'react';

import {
  ApolloQueryResult,
  OperationVariables,
  useLazyQuery,
  useMutation,
} from '@apollo/client';

import { useAuth } from './Auth';

import {
  ADD_WIDGET_TO_WORKSPACE,
  GET_WORKSPACE,
  LIST_USER_WORKSPACES,
  REMOVE_WIDGET_FROM_WORKSPACE,
  SET_STRATEGY_WORKSPACE,
  SET_WORKSPACE_WIDGET_POSITION,
  SET_WORKSPACE_WIDGETS_POSITIONS,
  SET_WORKSPACE_WIDGET_SIZE,
} from '../http/apollo-queries';

import { IWorkspace } from '../types/model';
import { logger } from '../helpers/logger';

interface IWorkspacesContext {
  workspace: IWorkspace;
  workspaces: IWorkspace[];
  userWorkspaces: IWorkspace[];
  defaultWorkspaces: IWorkspace[];
  getWorkspaceLoading: boolean;
  systemWorkspaces: IWorkspace[];
  workspaceLoading: boolean;
  widgetLoading: boolean;
  widgetAddLoading: boolean;
  widgetRemoveLoading: boolean;

  setStrategyWorkspace(
    variables?: Partial<OperationVariables> | undefined
  ): Promise<any>;

  setWorkspaceWidgetPosition(
    variables?: Partial<OperationVariables> | undefined
  ): Promise<any>;

  setWorkspaceWidgetsPositions(
    variables?: Partial<OperationVariables> | undefined
  ): Promise<any>;

  setWorkspaceWidgetSize(
    variables?: Partial<OperationVariables> | undefined
  ): Promise<any>;

  addWidgetToWorkspace(
    variables?: Partial<OperationVariables> | undefined
  ): Promise<any>;

  removeWidgetFromWorkspace(
    variables?: Partial<OperationVariables> | undefined
  ): Promise<any>;

  refetchWorkspace(
    variables?: Partial<OperationVariables> | undefined
  ): Promise<ApolloQueryResult<any>>;

  refetchWorkspaces(
    variables?: Partial<OperationVariables> | undefined
  ): Promise<ApolloQueryResult<any>>;
}

interface Props {
  children?: ReactNode;
  workspaceId: string;
}

const actions = {
  refetchWorkspaces: (): any => {},
  refetchWorkspace: (): any => {},
  setStrategyWorkspace: (): any => {},
  setWorkspaceWidgetSize: (): any => {},
  setWorkspaceWidgetPosition: (): any => {},
  setWorkspaceWidgetsPositions: (): any => {},
  removeWidgetFromWorkspace: (): any => {},
  addWidgetToWorkspace: (): any => {},
};

const WorkspacesContext = createContext<IWorkspacesContext>({
  workspace: {} as IWorkspace,
  workspaces: [],
  userWorkspaces: [],
  defaultWorkspaces: [],
  getWorkspaceLoading: true,
  systemWorkspaces: [],
  workspaceLoading: true,
  widgetLoading: false,
  widgetAddLoading: false,
  widgetRemoveLoading: false,
  ...actions,
});

export const useWorkspaces = () => useContext(WorkspacesContext);

export const WorkspacesProvider = ({ children, workspaceId }: Props) => {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);

  const [
    listWorkspaces,
    {
      data: { listWorkspaces: { items: workspaces = [] } = {} } = {},
      refetch: refetchWorkspaces,
    },
  ] = useLazyQuery<{ listWorkspaces: { items: IWorkspace[] } }>(
    LIST_USER_WORKSPACES,
    {
      variables: { profileId: user?.profile?.id },
      fetchPolicy: 'cache-and-network',
    }
  );

  const [
    getWorkspace,
    {
      data: { getWorkspace: workspace = {} } = {},
      loading: getWorkspaceLoading,
      refetch: refetchWorkspace,
    },
  ] = useLazyQuery(GET_WORKSPACE, {
    variables: { workspaceId },
    notifyOnNetworkStatusChange: true,
  });

  const [setStrategyWorkspace, { loading: setStrategyWorkspaceLoading }] =
    useMutation(SET_STRATEGY_WORKSPACE);

  const [addWidgetToWorkspace, { loading: widgetAddLoading }] = useMutation(
    ADD_WIDGET_TO_WORKSPACE
  );

  const [removeWidgetFromWorkspace, { loading: widgetRemoveLoading }] =
    useMutation(REMOVE_WIDGET_FROM_WORKSPACE);

  const [setWorkspaceWidgetSize] = useMutation(SET_WORKSPACE_WIDGET_SIZE);

  const [setWorkspaceWidgetPosition] = useMutation(
    SET_WORKSPACE_WIDGET_POSITION
  );

  const [setWorkspaceWidgetsPositions] = useMutation(
    SET_WORKSPACE_WIDGETS_POSITIONS
  );

  logger.log('🟠 WorkspacesContext:Workspaces', workspaces);

  useEffect(() => {
    if (user?.profile?.id) {
      Promise.all([getWorkspace(), listWorkspaces()])
        .catch((err) => {
          logger.error('🔴 WorkspacesContext:Workspaces', err);
          setLoading(false);
        })
        .then((res) => {
          setLoading(false);
        });
    }
  }, [user?.profile?.id, getWorkspace, listWorkspaces]);

  const value = {
    workspace,
    workspaces: workspaces.filter((w) => w.system || w.saved),
    userWorkspaces: workspaces.filter((w) => !w.system && w.saved),
    systemWorkspaces: workspaces.filter((w) => w.system && w.saved),
    defaultWorkspaces: workspaces.filter((w) => w.system && !w.saved),
    workspaceLoading: loading || setStrategyWorkspaceLoading,
    widgetLoading: widgetRemoveLoading || widgetAddLoading,
    widgetAddLoading,
    widgetRemoveLoading,
    getWorkspaceLoading,
    ...actions,
    refetchWorkspace,
    refetchWorkspaces,
    setStrategyWorkspace,
    setWorkspaceWidgetSize,
    setWorkspaceWidgetPosition,
    setWorkspaceWidgetsPositions,
    addWidgetToWorkspace,
    removeWidgetFromWorkspace,
  };

  return (
    <WorkspacesContext.Provider value={value}>
      {children}
    </WorkspacesContext.Provider>
  );
};
